/**
 * SimpleProviders
 *
 * Version complète des providers avec UIStateProvider
 * Pour toutes les apps (Quizz, Date, Form, Availability)
 */

import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { I18nextProvider } from "react-i18next";
import i18n from "../i18n";
import { TooltipProvider } from "./ui/tooltip";
import { AuthProvider } from "../contexts/AuthContext";
import { FeatureFlagsProvider } from "../contexts/FeatureFlagsContext";
import { OnboardingProvider } from "../contexts/OnboardingContext";
import { ConversationStateProvider } from "./conversation/ConversationStateProvider";
import { EditorStateProvider } from "./editor/EditorStateProvider";
import { ConversationProvider } from "./conversation/ConversationProvider";
import { UIStateProvider } from "./prototype/UIStateProvider";
import { QuotaModalProvider } from "../contexts/QuotaModalContext";
import { ProductProvider } from "../contexts/ProductContext";
import { ErrorBoundary } from "./ErrorBoundary";
import { Toaster } from "./ui/toaster";
import { Toaster as Sonner } from "./ui/sonner";
import { setupDynamicTitle } from "../lib/dynamic-title";
import { captureAttribution } from "../lib/utmTracking";
import { QuotaLimitModal } from "./modals/QuotaLimitModal";
import { GlobalQuotaHandler } from "./GlobalQuotaHandler";
import { SupportWidget } from "./support/SupportWidget";
import ScrollToTop from "./shared/ScrollToTop";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      retry: 1,
    },
  },
});

export const SimpleProviders = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // Configurer le titre dynamique
  React.useEffect(() => {
    try {
      setupDynamicTitle();
    } catch (error) {
      console.error("❌ setupDynamicTitle ERROR:", error);
    }
    // Capture l'attribution marketing (UTM + referer) au 1er chargement,
    // avant toute navigation SPA susceptible de purger la query string.
    // first-touch + idempotent : sûr à appeler à chaque montage.
    captureAttribution();
  }, []);
  try {
    return (
      <ErrorBoundary>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider
            attribute="class"
            defaultTheme="dark"
            enableSystem={false}
          >
            <QueryClientProvider client={queryClient}>
              <TooltipProvider>
                <AuthProvider>
                  <FeatureFlagsProvider>
                    <OnboardingProvider>
                      <ConversationStateProvider>
                        <EditorStateProvider>
                          <ConversationProvider>
                            <UIStateProvider>
                              <ProductProvider>
                                <QuotaModalProvider>
                                  <ScrollToTop />
                                  <QuotaLimitModal />
                                  <GlobalQuotaHandler />
                                  {children}
                                  <SupportWidget />
                                  <Toaster />
                                  <Sonner />
                                </QuotaModalProvider>
                              </ProductProvider>
                            </UIStateProvider>
                          </ConversationProvider>
                        </EditorStateProvider>
                      </ConversationStateProvider>
                    </OnboardingProvider>
                  </FeatureFlagsProvider>
                </AuthProvider>
              </TooltipProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </I18nextProvider>
      </ErrorBoundary>
    );
  } catch (error) {
    console.error(" SimpleProviders: ERROR during render:", error);
    throw error;
  }
};
